import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import clsx from 'clsx';
import { Box, IconButton, Typography, styled } from '@mui/material';
import {
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Info as InfoIcon,
  Report as ReportIcon,
  Warning as WarningIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useStore } from 'hooks';
import { useTranslation } from 'react-i18next';

import { dateUtilities } from 'utils';
import { ALARMS_TYPES } from 'config/alarmsType';

const conditionIcons = {
  1: <WarningIcon className="condition_icon priority_1" />,
  2: <ReportIcon className="condition_icon priority_2" />,
  3: <InfoIcon className="condition_icon priority_3" />,
};

const defaultnotificationBodyComposer = (_coolers_name, alarm, notification) =>
  `${_coolers_name} - ${alarm?.metadata?.original_sensor_name || alarm?.operand_id}`;

const notificationBodyComposerByAlarmType = {
  [ALARMS_TYPES.TEMPERATURE]: defaultnotificationBodyComposer,
  [ALARMS_TYPES.POUR]: defaultnotificationBodyComposer,
  [ALARMS_TYPES.SYSTEM]: () => '',
  [ALARMS_TYPES.FLOW_SENSOR_S]: (_coolers_name, alarm) => `- ${alarm?.operand_id}`,
  [ALARMS_TYPES.TEMP_SENSOR_S]: (_coolers_name, alarm) => `- ${alarm?.operand_id}`,
  [ALARMS_TYPES.LINE_CLEANING_DETECTED]: (
    _coolers_name,
    alarm,
    notification,
    { t, linesLabel },
  ) => {
    const { minFromDate, maxToDate } = dateUtilities.getMinMaxDates({
      arrayOfObjects: notification?._conditions_context?.cleanings,
    });
    const dateFrom = dateUtilities.formatDateIncludingToday(minFromDate);
    const dateTo = dateUtilities.formatDateIncludingToday(maxToDate);

    return `${t('shared.NotificationsMenu.from')}: ${dateFrom}\n${t(
      'shared.NotificationsMenu.to',
    )}: ${dateTo}\n${t('shared.NotificationsMenu.lines')}: ${linesLabel}`;
  },
};

const Item = ({ notification, onItemClick }) => {
  const { t } = useTranslation();
  const { status_code, _alarms_alert_caption, _coolers_name, resolved, id, alarm, created_at } =
    notification;

  const { notificationsStore, linesStore } = useStore();
  const lineIds = (notification?._conditions_context?.cleanings ?? []).map(e => e.line_id);
  const lineIdentifiers = [...linesStore.all, ...linesStore.expired]
    .filter(line => lineIds.includes(line.id))
    .map(line => line.line_identifier.replaceAll('Line-', ''));

  const cleaningLinesLabel = lineIdentifiers.join(', ');

  const handleClickItem = useCallback(() => {
    if (status_code === 0) notificationsStore.markAsRead([id]);

    onItemClick(id);
  }, [status_code, notificationsStore, id, onItemClick]);

  const lineCleaningPriority = notification.condition?.priority;

  const IconStatus = useMemo(() => {
    if (
      alarm?.type === ALARMS_TYPES.LINE_CLEANING_DETECTED &&
      conditionIcons[lineCleaningPriority]
    ) {
      return conditionIcons[lineCleaningPriority];
    }

    if (resolved) {
      return <Box className="resolved">{t('shared.NotificationsMenu.resolved')}</Box>;
    }

    if (status_code === 0) {
      return <WarningIcon />;
    }

    return <WarningAmberIcon />;
  }, [t, status_code, resolved, alarm?.type, lineCleaningPriority]);

  const notificationBody = useMemo(() => {
    if (!alarm?.type) return '';
    const composer =
      notificationBodyComposerByAlarmType[alarm.type] ?? defaultnotificationBodyComposer;
    return composer(_coolers_name, alarm, notification, { t, linesLabel: cleaningLinesLabel });
  }, [_coolers_name, alarm, notification, t, cleaningLinesLabel]);

  return (
    <StyledBox onClick={handleClickItem}>
      <Box className="first_row">
        {IconStatus}
        <Typography className={clsx('title', { unread: status_code === 0 })}>
          {t(`config.alertCaptions.${_alarms_alert_caption}`)}
        </Typography>
        <Box className={clsx('dot', { hidden: status_code !== 0 })} />
      </Box>
      <Box className="second_row">
        <Typography
          className={clsx('title', { unread: status_code === 0 })}
          sx={{ whiteSpace: 'pre-line' }}
        >
          {notificationBody}
        </Typography>
        <IconButton>
          <KeyboardArrowRightIcon className="arrow_icon" />
        </IconButton>
      </Box>
      <Box className="date_box">
        <Typography className="subtitle">
          {dateUtilities.formatDateIncludingToday(created_at)}
        </Typography>
      </Box>
    </StyledBox>
  );
};

export default observer(Item);

const StyledBox = styled(Box)(({ theme: { palette, breakpoints } }) => ({
  position: 'relative',
  padding: '16px 0',
  borderTop: '1px solid #313131',

  '.date_box': {
    width: '100%',
    display: 'flex',
    position: 'relative',
    top: '5px',
  },

  '.first_row': {
    display: 'flex',
    alignItems: 'center',

    svg: {
      color: palette.status.red,
      marginRight: '8px',
      height: '16px',
      width: '16px',
    },

    '.condition_icon': {
      '&.priority_1': { color: '#FF7A7A' },
      '&.priority_2': { color: '#FFCE44' },
      '&.priority_3': { color: '#66BB6A' },
    },

    '.dot': {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: palette.primary.main,
      marginLeft: '8px',
    },

    '.resolved': {
      color: palette.status.green,
      border: `1px solid ${palette.status.green}`,
      borderRadius: '36px',
      fontSize: '12px',
      padding: '2px 4px',
      marginRight: '8px',
    },
  },

  '.second_row': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  '.title': {
    fontSize: '14px',
    color: palette.whiteEmphasis.medium,

    '&.unread': {
      color: palette.whiteEmphasis.high,
    },
  },
  '.subtitle': {
    fontSize: '12px',
    color: palette.whiteEmphasis.medium,
  },

  '.arrow_icon': {
    color: palette.whiteEmphasis.high,
  },

  '.hidden': {
    display: 'none',
  },
}));
